import i18n from '@/i18n';
/**
 * Date and Time Utilities
 *
 * Handles conversion from UTC (database) to local timezone
 * and provides consistent formatting across the application.
 */

/** Truncate date to start of minute (floor). Used for runbook actualStart. */
export function truncateToMinuteFloor(date: Date): Date {
    return new Date(Math.floor(date.getTime() / 60000) * 60000);
}

/** Duration in whole minutes between two dates. Uses ceil so we never undercount. */
export function minutesBetween(start: Date, end: Date): number {
    return Math.ceil((end.getTime() - start.getTime()) / 60000);
}

/**
 * Convert UTC date string to local Date object
 * @param utcDateString - UTC date string from database (e.g., "2024-01-15T14:30:00Z")
 * @returns Local Date object
 */
export function utcToLocal(utcDateString: string): Date {
    return new Date(utcDateString);
}

/**
 * Format date for display in user's local timezone
 * @param utcDateString - UTC date string from database
 * @param options - Formatting options
 * @returns Formatted date string
 */
export function formatLocalDate(
    utcDateString: string,
    options: {
        includeTime?: boolean;
        includeTimezone?: boolean;
        format?: 'short' | 'medium' | 'long' | 'full';
    } = {}
): string {
    const {
        includeTime = true,
        includeTimezone = false,
        format = 'medium'
    } = options;

    const localDate = utcToLocal(utcDateString);

    const formatOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: format === 'short' ? 'short' : 'long',
        day: 'numeric',
    };

    if (includeTime) {
        formatOptions.hour = '2-digit';
        formatOptions.minute = '2-digit';
        formatOptions.hour12 = true;
    }

    if (includeTimezone) {
        formatOptions.timeZoneName = 'short';
    }

    return localDate.toLocaleDateString(appLocale(), formatOptions);
}

/**
 * Format time only in user's local timezone
 * @param utcDateString - UTC date string from database
 * @param includeSeconds - Whether to include seconds
 * @returns Formatted time string
 */
export function formatLocalTime(utcDateString: string, includeSeconds = false): string {
    const localDate = utcToLocal(utcDateString);

    const options: Intl.DateTimeFormatOptions = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    };

    if (includeSeconds) {
        options.second = '2-digit';
    }

    return localDate.toLocaleTimeString(appLocale(), options);
}

/**
 * Get relative time (e.g., "2 hours ago", "in 3 days")
 * @param utcDateString - UTC date string from database
 * @returns Relative time string
 */
export function getRelativeTime(utcDateString: string): string {
    const localDate = utcToLocal(utcDateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - localDate.getTime()) / 1000);

    if (diffInSeconds < 60) {
        return 'just now';
    } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 2592000) {
        const days = Math.floor(diffInSeconds / 86400);
        return `${days} day${days !== 1 ? 's' : ''} ago`;
    } else {
        return formatLocalDate(utcDateString, { includeTime: false });
    }
}

/**
 * Get user's timezone information
 * @returns Timezone information object
 */
export function getUserTimezone() {
    const now = new Date();
    return {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        offset: now.getTimezoneOffset(), // in minutes
        offsetHours: now.getTimezoneOffset() / 60, // in hours
        isDST: isDaylightSavingTime(now),
    };
}

/**
 * Check if a date is in daylight saving time
 * @param date - Date to check
 * @returns True if in DST
 */
function isDaylightSavingTime(date: Date): boolean {
    const jan = new Date(date.getFullYear(), 0, 1);
    const jul = new Date(date.getFullYear(), 6, 1);
    return date.getTimezoneOffset() < Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
}

/**
 * Convert local date to UTC for sending to database
 * @param localDate - Local Date object
 * @returns UTC date string for database
 */
export function localToUtc(localDate: Date): string {
    return localDate.toISOString();
}

/**
 * Format date for different locales
 * @param utcDateString - UTC date string from database
 * @param locale - Locale code. Defaults to the APP's language, not 'en-US' and not the
 *                 browser's: four of this function's callers relied on that default and were
 *                 rendering American dates in every language.
 * @param options - Formatting options
 * @returns Formatted date string in specified locale
 */
export function formatDateForLocale(
    utcDateString: string,
    locale: string = appLocale(),
    options: Intl.DateTimeFormatOptions = {}
): string {
    const localDate = utcToLocal(utcDateString);

    const defaultOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    };

    return localDate.toLocaleDateString(locale, { ...defaultOptions, ...options });
}

// ── ONE DATE FORMAT FOR THE APP ────────────────────────────────────────────────
//
// `date.toLocaleDateString()` with no locale uses the BROWSER's locale, not the app's. Switching
// the language in the product changed every label on the screen except the dates, which kept
// rendering 08/09/2026 whatever you picked. Passing the i18n language is the whole fix, and the
// reason these live in one place is that there is no way to spot the bare call by looking at the
// screen — it renders a perfectly plausible date, just not the one the language asked for.
//
// The shape is day / short month / year: 08 Sep 2026, not 08/09/2026. A numeric day-month is
// ambiguous across exactly the audience this product has — 08/09 is September in London and
// August in New York — and there is no way for a reader to tell which they are looking at. A
// named month cannot be misread.
//
// 24-hour time, always. This is infrastructure tooling where timestamps get compared, quoted in
// runbooks and read off during a recovery; am/pm is one more thing to get wrong at 3am.
//
// The MONTH NAME still follows the language: "08 Sep 2026" in English, "08. Sept. 2026" in
// German, "٠٨ سبتمبر ٢٠٢٦" in Arabic. That is what "the formatting should change with the
// language" means — the shape is ours, the words and the numerals are the locale's.

/**
 * The language the APP is set to, which is not the language the browser is set to.
 *
 * Read from the live i18next instance rather than passed down through every component, because
 * the alternative was threading a locale through forty-nine files — and a component that gets it
 * wrong produces a plausible date rather than an error, which is precisely the failure this whole
 * module exists to stop. Every screen using these helpers also renders translated text, so it is
 * already subscribed to i18n and re-renders when the language changes.
 *
 * Exported so a call that needs its OWN options (a chart axis wanting "8 Sep", not a full
 * timestamp) can still be in the right language: `d.toLocaleDateString(appLocale(), { ... })`.
 */
export function appLocale(): string {
    return i18n.language || 'en-GB';
}

const DATE_PARTS: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
const TIME_PARTS: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: false };

/** Nothing to show is a dash, never "Invalid Date" — which is what `new Date(null)` renders. */
const EMPTY = '—';

function toDate(value: string | number | Date | null | undefined): Date | null {
    if (value === null || value === undefined || value === '') return null;
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}

/** "08 Sep 2026" in the given language. */
export function formatDate(
    value: string | number | Date | null | undefined,
    locale: string = appLocale(),
): string {
    const d = toDate(value);
    return d ? d.toLocaleDateString(locale, DATE_PARTS) : EMPTY;
}

/** "08 Sep 2026 16:08" in the given language. */
export function formatDateTime(
    value: string | number | Date | null | undefined,
    locale: string = appLocale(),
): string {
    const d = toDate(value);
    if (!d) return EMPTY;
    return `${d.toLocaleDateString(locale, DATE_PARTS)} ${d.toLocaleTimeString(locale, TIME_PARTS)}`;
}

/** "16:08" in the given language. */
export function formatTime(
    value: string | number | Date | null | undefined,
    locale: string = appLocale(),
): string {
    const d = toDate(value);
    return d ? d.toLocaleTimeString(locale, TIME_PARTS) : EMPTY;
}
